import type React from 'react';
import type { Notification } from '../services/notificationService';
import {
    BellIcon,
    TruckIcon,
    ArrowsRightLeftIcon,
    ShoppingBagIcon,
    ArrowsUpDownIcon,
    Cog6ToothIcon,
} from '@heroicons/react/20/solid';

export interface NotificationCategory {
    value: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    iconClass: string;
    // Notification `type` values that belong to this category. Sync notifications come as
    // either SQL_ACCOUNT_SYNC (stock/party sync) or SQL_ACCOUNT_DO_QR_SYNC (DO/QR sync) -
    // both group under "SQL Account Sync" here.
    matchTypes: string[];
    placeholder?: boolean;
}

// Only TRACKING_STALE and SQL_ACCOUNT_SYNC/SQL_ACCOUNT_DO_QR_SYNC are actually generated
// today. The rest are placeholder categories for future notification types, shown
// greyed-out with a 0 count.
export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
    { value: 'TRACKING_STALE', label: 'Tracking', icon: TruckIcon, iconClass: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', matchTypes: ['TRACKING_STALE'] },
    { value: 'SQL_ACCOUNT_SYNC', label: 'SQL Account Sync', icon: ArrowsRightLeftIcon, iconClass: 'bg-sky-500/15 text-sky-600 dark:text-sky-400', matchTypes: ['SQL_ACCOUNT_SYNC', 'SQL_ACCOUNT_DO_QR_SYNC'] },
    { value: 'STOCK', label: 'Stock Alerts', icon: ShoppingBagIcon, iconClass: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400', matchTypes: [], placeholder: true },
    { value: 'ORDERS', label: 'Orders', icon: ArrowsUpDownIcon, iconClass: 'bg-violet-500/15 text-violet-600 dark:text-violet-400', matchTypes: [], placeholder: true },
    { value: 'SYSTEM', label: 'System', icon: Cog6ToothIcon, iconClass: 'bg-zinc-500/15 text-zinc-600 dark:text-zinc-400', matchTypes: [], placeholder: true },
];

export const DEFAULT_NOTIFICATION_ICON = BellIcon;

export function findNotificationCategory(type: string): NotificationCategory | undefined {
    return NOTIFICATION_CATEGORIES.find((c) => c.matchTypes.includes(type));
}

export function findNotificationCategoryByValue(value: string): NotificationCategory | undefined {
    return NOTIFICATION_CATEGORIES.find((c) => c.value === value);
}

export function formatNotificationRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function startOfDay(d: Date): Date {
    const c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c;
}

export function groupNotificationsByTimeline(items: Notification[]): { label: string; items: Notification[] }[] {
    const today = startOfDay(new Date());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const order: string[] = [];
    const buckets = new Map<string, Notification[]>();
    for (const n of items) {
        const d = startOfDay(new Date(n.created_time));
        let label: string;
        if (d.getTime() === today.getTime()) label = 'Today';
        else if (d.getTime() === yesterday.getTime()) label = 'Yesterday';
        else label = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });

        if (!buckets.has(label)) {
            buckets.set(label, []);
            order.push(label);
        }
        buckets.get(label)!.push(n);
    }
    return order.map((label) => ({ label, items: buckets.get(label)! }));
}
